/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/faceDecal.js
 *
 * Khari, 2026-08-31: *"turn on the video faces, and make sure they animate when
 * the avatar is chatting/talking."* The mouth decal is the most tuned code on
 * the platform — REF_BOX, the oval-in-clip-space mask, the flat-projection
 * facing cull, the fit affine — and every one of those was paid for by a
 * rendering bug that took days to read. Re-deriving it here would have meant
 * paying for all of them a second time.
 *
 * ⚠ WHY A COPY AND NOT AN IMPORT. The World is a SEPARATE vite app with its own
 * node_modules, deployed on its own to world.hypetunes.com. A relative import
 * across repos builds on this machine and dies on any box that has only one
 * checkout; a vite alias is the same trap with a nicer spelling. There is no
 * workspace and no private registry, so a package is a bigger change than the
 * feature. This is the same coupling `src/poses.js` has with the API's
 * `PlatePoseSpec` — and that one is documented there as
 * "🔴 NOTHING ENFORCES IT", which is exactly the failure being avoided here.
 *
 * ⚠ SO IT IS ENFORCED. `npm run check:face` (and `npm run dev`, via `predev`)
 * compares everything below the sentinel against the Web original and FAILS on
 * any difference. Change one, change both — and now the machine says so.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then re-copy.
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
import * as THREE from 'three'

/**
 * A MOUTH, CUT FROM HER OWN FACE.
 *
 * Khari, 2026-08-28: *"build a second skinned mesh from exactly those, share her
 * skeleton, give it the video material. This is exactly what I'd like to do."*
 *
 * ── WHY THIS AND NOT A PANEL ───────────────────────────────────────────────
 * No body on the platform can move its mouth: 26 joints, no jaw, zero
 * blendshapes, on all 17 of them. The obvious workaround is a plane parented to
 * the head bone with a video on it — and it reads as a sticker the moment she is
 * not dead-on, because a flat card over a curved face has an edge and she turns
 * constantly.
 *
 * A decal cut from the body's OWN triangles has no edge and no alignment
 * problem: it is her face, at her face's curvature, in her face's position. It
 * shares her skeleton and her bind matrix, so it deforms with every clip for
 * free — nothing has to track anything.
 *
 * ── WHY THE SELECTION IS IN 3D AND NOT IN UV ───────────────────────────────
 * ⚠ THE ATLAS IS FRAGMENTED. Measured on avatar 19's hero body: the triangles
 * around the mouth occupy 99.6% × 99.2% of the texture sheet — the auto-unwrap
 * scatters neighbouring faces across the whole atlas, so "the mouth's UV
 * rectangle" does not exist. Selecting by UV would have taken most of the body.
 * So triangles are chosen by WHERE THEY ARE, in a box anchored to the head
 * bone and measured in skull heights, which also makes the same numbers work on
 * a body of a different size.
 *
 * ⚠ AND THE DECAL GETS FRESH UVs. Reusing the atlas UVs would map the video
 * through that same scattering — every triangle sampling a random part of the
 * clip. Instead the patch is projected flat along the face direction, which is
 * how you would tape a picture to someone's face and is exactly what a
 * front-on mouth video wants.
 */

/** The head's own axes, in mesh space, from the rig rather than from a guess. */
function faceFrame(skinned, yaw = 0) {
  const bones = skinned.skeleton?.bones || []
  const by = (suffix) => bones.find((b) => b.name.toLowerCase().endsWith(suffix)) || null
  const head = by('head')
  const crown = by('head_end')
  const front = by('headfront')
  if (!head || !crown) return null

  // Bind-pose positions in mesh space: the inverse bind matrix maps mesh space
  // into a bone's bind space, so its inverse puts the bone back among the
  // vertices — whatever unit scale sits between the armature and the mesh.
  // (Walking node translations instead reads centimetres against a metre mesh.)
  const at = (bone) => {
    const i = bones.indexOf(bone)
    const m = new THREE.Matrix4().copy(skinned.skeleton.boneInverses[i]).invert()
    return new THREE.Vector3().setFromMatrixPosition(m)
  }
  const H = at(head)
  const C = at(crown)
  const skull = Math.max(C.distanceTo(H), 1e-4)

  let dir = new THREE.Vector3(0, 0, 1)
  if (front) {
    const F = at(front).sub(H)
    F.y = 0
    if (F.lengthSq() > 1e-8) dir = F.normalize()
  }
  /* ⚠ AND THE BONE CAN BE WRONG, WHICH NOTHING ELSE HERE WOULD CATCH. Khari,
     2026-08-29: *"its amount of rotation OFF is close to the same 33 degrees or
     so that the characters stand at before we direct them."*

     `headfront` is placed by the rigger, and on some bodies it lands on the
     RIG's forward axis rather than on the face — so a body bound standing at an
     angle reports a face direction it does not have. The projection is flat
     along this vector, so being off by an angle SHEARS the clip across the
     cheek; it does not merely slide it. That is why correcting it with `Clip X`
     gets the middle of the face to line up and never quite fixes the edges — a
     lateral offset cannot cancel an angular error.

     Measured in degrees about Y, stored on the body like every other value that
     depends on how one reconstruction came out. 0 means "trust the bone". */
  if (yaw) {
    const a = (yaw * Math.PI) / 180
    const c = Math.cos(a); const s2 = Math.sin(a)
    dir = new THREE.Vector3(dir.x * c + dir.z * s2, 0, -dir.x * s2 + dir.z * c).normalize()
  }
  /* ⚠ THE VIEWER'S RIGHT, NOT AN ARBITRARY PERPENDICULAR. There are two
     perpendiculars to the face direction and they differ by a mirror; the one
     that matters is the one that increases toward SCREEN RIGHT, because a video
     of a mouth is a picture taken from where the viewer stands. With the face
     pointing +Z the camera looks down −Z, so screen-right is +X — and
     `(-dir.z, 0, dir.x)` gives −X, which flipped every decal left-for-right.
     Invisible on a symmetric mouth, wrong the moment there is a mole, a
     piercing or a crooked lip. */
  const side = new THREE.Vector3(dir.z, 0, -dir.x)
  return { head: H, skull, dir, side }
}

/* ── THE FRAME THE CLIP IS MEASURED IN ──────────────────────────────────────
   ⚠ NOT THE PATCH. Khari, 2026-08-29: *"when I change the patch width, I
   expect it to add more coverage area, but the video is scaling with it."*

   The patch and the clip's fit inside it were split once already, but only
   halfway: `scale`/`offsetX`/`offsetY` were added as a MAGNIFIER on top of a
   base that still normalised the UVs to the kept vertices' own extent. So the
   clip always stretched to fill whatever was cut, and every patch slider was
   also a clip slider — widen the cut, and the same video spread across it.

   The cure is a frame that no slider touches: the patch AS IT WOULD BE at the
   fixed box below, measured on this body's own head so it still adapts to skull
   size and shape. Coverage is then `y0/y1/halfWidth/front` alone, and size is
   `scale` alone.

   ⚠ THESE NUMBERS ARE AVATAR 19'S TUNED BOX, ON PURPOSE. Every stored face was
   tuned against the old patch-relative frame; making the reference equal to the
   box they were tuned at means the mapping is UNCHANGED where it was already
   right, and only starts diverging as a slider is moved. Changing them
   retroactively resizes every face already dialled in. */
const REF_BOX = { y0: -0.13, y1: 0.44, halfWidth: 0.22, front: 0.55 }
/* Measured once per body per facing rule: it depends on the mesh, never on the
   patch sliders, and a slider drag re-cuts the patch on every frame it moves. */
const REF_CACHE = new WeakMap()

/**
 * Cut the patch and return a SkinnedMesh bound to the same skeleton.
 *
 * `box` is in SKULL HEIGHTS relative to the head bone:
 *   y0/y1      how far up the skull the band runs (0 = the head bone itself)
 *   halfWidth  lateral half-extent, either side of centre
 *   front      keep only this fraction of the head's depth, from the front
 *
 * Returns null when nothing matched, which is a real answer — a body whose head
 * is shaped differently should show no mouth rather than a patch of neck.
 */
export function buildFaceDecal(skinned, box = {}) {
  const {
    y0 = 0.06, y1 = 0.24, halfWidth = 0.18, front = 0.55,
    /* ── HOW THE CLIP SITS INSIDE THE PATCH ────────────────────────────────
       Khari, 2026-08-28: *"i need params so I can enlarge the face scale."*

       The patch's SHAPE (the four numbers above) and the clip's FIT inside it
       are different problems and were tangled together: the only way to make a
       generated face bigger was to shrink the patch, which also moved which
       triangles were cut and therefore where the silhouette fell.

       These three move the picture, not the patch. `scale` above 1 magnifies —
       the patch samples a smaller, central part of the clip — and the offsets
       slide it, which is what the last few millimetres of eyebrow alignment
       need.

       ⚠ THEY ARE FRACTIONS OF THE REFERENCE FRAME, NOT OF THE PATCH. That is
       the whole point of REF_BOX below: measured against the patch, `scale 1`
       meant "however wide the cut happens to be", so the patch sliders resized
       the clip. Against a fixed frame, `scale 1` is one fixed size on this
       head and the patch sliders only decide how much of it is shown. */
    scale = 1, offsetX = 0, offsetY = 0,
    /* ⚠ WITHOUT THIS THE CLIP IS DRAWN TWICE. Khari, 2026-08-29: *"I see the
       video repeated on the back of the avatar's head."*

       The patch is projected FLAT along the face direction, so two vertices at
       the same height and the same lateral offset get identical UVs however far
       apart they are front-to-back — the back of the skull samples the same
       pixels as the face. Measured on the tuned reference box: 16% of avatar
       22's kept triangles and 17% of avatar 19's face AWAY from the camera.
       They were invisible only because the oval hid the periphery and the
       material is FrontSide, so the copy showed up the moment the body turned.

       Kept as a number rather than a boolean because the silhouette is where a
       flat projection smears worst: raise it to trim the grazing triangles too.
       0 is the front hemisphere. -1 disables the test.
       ⚠ COSTS ALMOST NO COVERAGE — the culled triangles sit BEHIND kept ones at
       the same lateral offset, so the patch's measured extent moves 0-0.5%. */
    facing = 0,
    // Degrees to turn the projection axis about Y when `headfront` is not on the
    // face. See faceFrame.
    faceYaw = 0,
  } = box
  const frame = faceFrame(skinned, faceYaw)
  if (!frame) return null
  const { head, skull, dir, side } = frame

  const geo = skinned.geometry
  const pos = geo.getAttribute('position')
  const nor = geo.getAttribute('normal')
  const ji = geo.getAttribute('skinIndex')
  const jw = geo.getAttribute('skinWeight')
  const index = geo.getIndex()
  if (!pos || !index) return null

  // The head's depth range, so `front` is a fraction of the head rather than a
  // distance nobody can reason about.
  const v = new THREE.Vector3()
  let dMin = Infinity
  let dMax = -Infinity
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i)
    if (v.y < head.y || v.y > head.y + skull) continue
    const d = v.dot(dir)
    if (d < dMin) dMin = d
    if (d > dMax) dMax = d
  }
  if (!Number.isFinite(dMin)) return null

  /* Cut one box out of the head and measure what came back. Factored out
     because it now runs TWICE — once for the box being tuned, once for the
     fixed reference box — and the two must be cut by identical rules or the
     frame drifts against the patch it is meant to be independent of. */
  const cutBox = ({ y0: bY0, y1: bY1, halfWidth: bHalf, front: bFront }) => {
    const yLo = head.y + skull * bY0
    const yHi = head.y + skull * bY1
    const dCut = dMin + (dMax - dMin) * bFront
    const xLim = skull * bHalf

    const inside = (i) => {
      v.fromBufferAttribute(pos, i)
      if (v.y < yLo || v.y > yHi) return false
      if (v.dot(dir) < dCut) return false
      if (Math.abs(v.clone().sub(head).dot(side)) > xLim) return false
      // Facing last: it is the only test that needs a second attribute read.
      if (nor && facing > -1) {
        v.fromBufferAttribute(nor, i)
        if (v.dot(dir) <= facing) return false
      }
      return true
    }

    // ⚠ ALL THREE CORNERS, not the centroid. A triangle with one vertex inside
    // hangs half out of the patch, and a decal with a ragged silhouette is the
    // sticker edge this whole approach exists to avoid.
    const keep = []
    const cache = new Map()
    const test = (i) => { if (!cache.has(i)) cache.set(i, inside(i)); return cache.get(i) }
    for (let t = 0; t < index.count; t += 3) {
      const a = index.getX(t)
      const b = index.getX(t + 1)
      const c = index.getX(t + 2)
      if (test(a) && test(b) && test(c)) keep.push(a, b, c)
    }
    if (!keep.length) return null

    // Extent of what was actually kept, along the face and up it.
    let lo0 = Infinity; let lo1 = Infinity; let hi0 = -Infinity; let hi1 = -Infinity
    for (const i of keep) {
      v.fromBufferAttribute(pos, i)
      const s = v.clone().sub(head).dot(side)
      if (s < lo0) lo0 = s
      if (s > hi0) hi0 = s
      if (v.y < lo1) lo1 = v.y
      if (v.y > hi1) hi1 = v.y
    }
    // Guarded: a degenerate span would divide the whole patch to infinity.
    const w = Math.max(hi0 - lo0, 1e-6)
    const h = Math.max(hi1 - lo1, 1e-6)
    /* ⚠ THE LATERAL FRAME IS FORCED SYMMETRIC ABOUT THE FACE'S MIDLINE. Khari,
       2026-08-29: *"the patch itself is offcenter... it should spread from the
       center of the face, not grow to the left side."*

       Measured: the patch IS centred to a tenth of a millimetre for as long as
       `halfWidth` is what limits it. Past that the box is wider than the head is
       at this depth, so the only triangles left to add are wherever the head
       still has surface in front of the cut — 20.6mm on avatar 22's left
       against 13.1mm on his right — and the bounding box drifts off-centre with
       them. The MASK is inscribed in that box, so the visible window slid
       sideways even though the video had not moved.

       Taking the SMALLER half keeps the oval on the midline and inside the kept
       triangles on both sides. The wider side's triangles are not thrown away;
       they simply fall outside the oval, which is where the fade already is. */
    const symHalf = lo0 < 0 && hi0 > 0 ? Math.min(-lo0, hi0) : null
    return {
      keep, lo0, lo1, w, h, cx: lo0 + w / 2, cy: lo1 + h / 2, symHalf,
      // What the cut hit, so a caller can say WHY a slider stopped working.
      left: -lo0, right: hi0, xLim, limitedByBox: Math.max(-lo0, hi0) >= xLim * 0.98,
    }
  }

  const patch = cutBox({ y0, y1, halfWidth, front })
  if (!patch) return null
  const { keep } = patch

  /* ⚠ FALLS BACK TO THE PATCH, not to nothing. A head the reference box misses
     entirely — a very small or oddly proportioned one — would otherwise get a
     zero-span frame and show a single texel stretched over the whole face. */
  /* ⚠ KEYED BY `facing` TOO, NOT JUST BY THE MESH. The reference cut obeys the
     same facing test as the patch, so caching it per-geometry alone would hand
     back a frame cut under the old rule the moment that value changed — and a
     stale reference frame silently RESIZES THE CLIP, which is the exact failure
     this frame exists to prevent. */
  let byFacing = REF_CACHE.get(geo)
  if (!byFacing) { byFacing = new Map(); REF_CACHE.set(geo, byFacing) }
  const refKey = `${facing}|${faceYaw}`
  let ref = byFacing.get(refKey)
  if (!ref) {
    ref = cutBox(REF_BOX) || patch
    byFacing.set(refKey, ref)
  }

  // Re-index: only the vertices actually used, so the decal is a few hundred
  // verts rather than a copy of a 140k-vertex body with holes in it.
  const remap = new Map()
  const P = []
  const N = []
  const UV = []
  const SI = []
  const SW = []
  const idx = []
  // Guarded: a zero or negative scale would divide the whole patch to infinity.
  const sc = Math.max(0.05, Number.isFinite(scale) ? scale : 1)
  /* Where the clip lands INSIDE the patch, in the clip's own 0..1. The single
     most diagnostic pair of numbers there is: a span far past 1 means the patch
     is much bigger than the video, so everything beyond it clamps to the video's
     border — a dark ragged halo that reads as a broken cut rather than as a
     `scale` that is too low for this patch. */
  let uMin = Infinity; let uMax = -Infinity; let vMin = Infinity; let vMax = -Infinity

  for (const i of keep) {
    let n = remap.get(i)
    if (n === undefined) {
      n = remap.size
      remap.set(i, n)
      v.fromBufferAttribute(pos, i)
      P.push(v.x, v.y, v.z)
      /* ⚠ FLAT PROJECTION ALONG THE FACE, AND V IS NOT INVERTED HERE.
         The first version subtracted V from 1 "because image space counts down
         while the head counts up" — which is true of image FILES and already
         handled: three.js sets `flipY = true` on textures by default, so v=0 is
         the bottom of the picture as you see it. Inverting again put the mouth
         on upside down (Khari: *"I can see the mouth video is upside down"*),
         with the lower lip on top. V rises with her head; the texture's own
         flip does the rest. */
      const s = v.clone().sub(head).dot(side)
      /* ⚠ THE TRANSFORM IS INVERTED, because it moves the PICTURE and what is
         being computed is where to SAMPLE it. To show the clip's content
         shifted up by `offsetY`, this vertex must read from a point that much
         further DOWN the clip — hence subtracting the offset and dividing by
         the scale rather than the reverse. */
      const u = ((s - ref.cx) / ref.w - offsetX) / sc + 0.5
      const vv = ((v.y - ref.cy) / ref.h - offsetY) / sc + 0.5
      if (u < uMin) uMin = u
      if (u > uMax) uMax = u
      if (vv < vMin) vMin = vv
      if (vv > vMax) vMax = vv
      UV.push(u, vv)
      if (nor) { v.fromBufferAttribute(nor, i); N.push(v.x, v.y, v.z) }
      if (ji) SI.push(ji.getX(i), ji.getY(i), ji.getZ(i), ji.getW(i))
      if (jw) SW.push(jw.getX(i), jw.getY(i), jw.getZ(i), jw.getW(i))
    }
    idx.push(n)
  }

  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3))
  if (N.length) g.setAttribute('normal', new THREE.Float32BufferAttribute(N, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(UV, 2))
  if (SI.length) g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(SI, 4))
  if (SW.length) g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(SW, 4))
  g.setIndex(idx)
  if (!N.length) g.computeVertexNormals()

  /* ⚠ THE MASK CANNOT DERIVE THIS FROM `scale` ANY MORE. The feather is drawn
     in the clip's space but has to land on the PATCH's border, so it needs the
     map from one to the other — and now that the clip is measured in the
     reference frame, that map depends on how far the patch has been moved away
     from it. Handed over as a plain affine `patch = a·uv + b` per axis so the
     mask does not need to know what a reference box is. */
  const mLo = patch.symHalf != null ? -patch.symHalf : patch.lo0
  const mW = patch.symHalf != null ? patch.symHalf * 2 : patch.w
  const fit = {
    ax: (sc * ref.w) / mW,
    bx: ((offsetX - 0.5 * sc) * ref.w + ref.cx - mLo) / mW,
    ay: (sc * ref.h) / patch.h,
    by: ((offsetY - 0.5 * sc) * ref.h + ref.cy - patch.lo1) / patch.h,
  }

  return {
    geometry: g,
    triangles: keep.length / 3,
    vertices: remap.size,
    frame,
    fit,
    /* For the panel. `limitedByBox` false means `halfWidth` is no longer doing
       anything — the head ran out before the box did, and `front` is the
       control that adds coverage from there. Without this the slider looks
       broken rather than finished. */
    diag: {
      triangles: keep.length / 3,
      left: patch.left,
      right: patch.right,
      xLim: patch.xLim,
      limitedByBox: patch.limitedByBox,
      skull,
      // Fraction of the patch the clip actually covers, and where its centre sits.
      clipAcross: 1 / Math.max(uMax - uMin, 1e-6),
      clipDown: 1 / Math.max(vMax - vMin, 1e-6),
      clipCentre: (uMin + uMax) / 2,
    },
  }
}

/**
 * Attach a decal to the body so it moves with it.
 *
 * ⚠ SAME PARENT, SAME SKELETON, SAME BIND MATRIX. A SkinnedMesh reads its
 * vertices in bind space; give it a different bind matrix and it deforms
 * correctly but sits somewhere else entirely. Sharing all three is what makes
 * this a patch of her face rather than an object near it.
 *
 * ⚠ `polygonOffset` IS NOT OPTIONAL. The decal is coplanar with the skin it was
 * cut from, so without a depth bias the two fight for every pixel and the patch
 * flickers as she moves — z-fighting reads as a broken texture, not as a
 * near-miss.
 */
export function attachFaceDecal(skinned, geometry, material) {
  const mesh = new THREE.SkinnedMesh(geometry, material)
  mesh.name = 'face-decal'
  mesh.bind(skinned.skeleton, skinned.bindMatrix)
  mesh.bindMode = skinned.bindMode
  // It is small, it is always where the head is, and culling it costs more than
  // drawing it.
  mesh.frustumCulled = false
  mesh.renderOrder = 1
  skinned.parent.add(mesh)
  return mesh
}

/** A material that makes the patch impossible to miss — for finding the box. */
export function debugDecalMaterial() {
  return new THREE.MeshBasicMaterial({
    color: 0xff2e6a,
    transparent: true,
    opacity: 0.85,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    side: THREE.DoubleSide,
  })
}

/**
 * A SOFT BORDER, PAINTED INTO AN ALPHA MASK.
 *
 * Khari, 2026-08-28: *"can you feather the edges?"*
 *
 * ⚠ THE EDGE IS TWO PROBLEMS AT ONCE and this solves both. The patch is a
 * RECTANGLE in projection but its silhouette follows whole triangles, so it
 * ends in a stair-step; and even a perfect edge would still be a hard line
 * between video and skin. Fading the last fifth of the patch to nothing hides
 * the steps (they all live at the border, which is now transparent) and lets
 * her real face show through underneath, which is what makes it read as her
 * mouth rather than as something laid over it.
 *
 * ⚠ AN `alphaMap`, NOT A SHADER PATCH AND NOT VERTEX ALPHA. It samples the same
 * UVs the video does, so the falloff is in the patch's own space and stays
 * correct whatever box is chosen. Vertex alpha would have been quantised to a
 * few hundred vertices; `onBeforeCompile` would have been a fork of the
 * standard material to express a gradient.
 *
 * ⚠ NOT sRGB. A mask is data, not colour — decoding it would bend the ramp.
 */
function featherAlphaMap(feather, shape = 'oval', fit = {}) {
  /* ⚠ SQUARE, AND THE RESOLUTION IS NOT THE PATCH'S. UVs are normalised 0..1
     on both axes, so an ellipse inscribed in this square lands as an ellipse
     inscribed in the patch whatever the patch's real proportions are. The size
     only decides how smooth the ramp is. */
  const W = 256
  const H = 256
  /* How much of the PATCH's border fades, as a fraction of it. Small: this is
     only hiding a stair-step of triangle edges, and a wide band here would eat
     into the face the oval is trying to show. */
  const PATCH_FADE = 0.06
  /* ⚠ A PLAIN BUFFER, NOT A CANVAS, AND THAT IS THE MOBILE PORT'S ONE DEMAND ON
     THIS FILE. Everything else here is arithmetic and three.js, which React
     Native runs unchanged — `document.createElement('canvas')` is the single
     line that cannot exist on a runtime with no DOM. A `DataTexture` over a
     typed array is the same pixels by a shorter route, so the whole file now
     copies verbatim to the app rather than being re-typed there (see
     `Mobile/scripts/check_shared_3d_sync.mjs`).

     ⚠ AND THE ROWS ARE WRITTEN BOTTOM-UP, WHICH IS NOT A DETAIL. A CanvasTexture
     carries `flipY = true`, so the canvas's TOP row was uploaded as the
     texture's LAST — i.e. what this loop calls `v` has always been sampled as
     `1 - v`. `DataTexture` defaults to `flipY = false`, and WebGL's
     `UNPACK_FLIP_Y_WEBGL` is not dependable for typed-array uploads anyway, so
     the flip is done HERE, in the write index, where it is explicit and works
     the same on both renderers. Getting this wrong would mirror the mask
     vertically — invisible on a symmetric one, and this one is deliberately not:
     `featherTop` widens the fade band at one end only. */
  const data = new Uint8Array(W * H * 4)
  const f = Math.min(0.9, Math.max(0.001, feather))
  const smooth = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t) }
  /* ⚠ THE MASK IS DRAWN IN THE CLIP'S SPACE, NOT THE PATCH'S — and that is the
     correction, not the convenience. `scale` and the offsets are baked into the
     vertex UVs, and an alphaMap samples those same UVs, so a clip magnified to
     1.25 was shrinking the oval to 1/1.25 along with it: the feather stopped
     following the patch's border and left a hard ring of un-faded video around
     a smaller window. Khari, 2026-08-28: *"please remember to feather the edges
     as well."*

     So the oval is evaluated at the PATCH coordinate that each clip coordinate
     corresponds to — the inverse of the transform applied to the UVs — and the
     border stays exactly on the patch's edge at any scale or offset. Baked into
     the canvas rather than set as a texture matrix so it does not depend on
     per-map UV transforms being supported. */
  /* ⚠ THE OVAL LIVES IN THE CLIP'S SPACE, NOT THE PATCH'S — and getting that
     wrong is what made the decal a hard-edged block. Khari, 2026-08-29: *"it
     seems like you had it right for a moment, and then it regressed."*

     It was right while the clip FILLED the patch, because the two spaces were
     then the same thing and an oval inscribed in one was inscribed in the other.
     Decoupling them broke that identity and the mask was left behind in patch
     space: with a patch much larger than the clip, the whole fade band falls
     outside the video, so the clip draws at full alpha with a hard edge and the
     clamped border around it stays opaque. That block IS the bug.

     The oval is a crop of the VIDEO, so it belongs in the video's coordinates —
     which is exactly what this map is indexed by. The affine is still needed,
     but only for the second term below. */
  const ax = Number.isFinite(fit.ax) ? fit.ax : 1
  const bx = Number.isFinite(fit.bx) ? fit.bx : 0
  const ay = Number.isFinite(fit.ay) ? fit.ay : 1
  const by = Number.isFinite(fit.by) ? fit.by : 0
  const ov = Math.max(0.2, Number.isFinite(fit.oval) ? fit.oval : 1)
  // Vertical radius. Defaults to the horizontal one, so a single `oval` still
  // means a circle-in-the-patch exactly as before.
  const ovY = Math.max(0.2, Number.isFinite(fit.ovalY) ? fit.ovalY : ov)
  // Defaults to the base feather, so leaving it out changes nothing.
  const fTop = Math.min(0.9, Math.max(0.001, Number.isFinite(fit.featherTop) ? fit.featherTop : f))
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      // The clip's own 0..1 — the coordinates the mesh's UVs actually carry.
      const u = (x + 0.5) / W
      const v = (y + 0.5) / H
      let a
      if (shape === 'rect') {
        a = smooth(Math.min(u, 1 - u) / f) * smooth(Math.min(v, 1 - v) / f)
      } else {
        /* ⚠ AN OVAL, AND IT IS THE DEFAULT. Khari, 2026-08-28: *"we can feather
           out a lot of it, and probably get away with just showing the front
           portion of the face in an oval or soft rectangle area."*

           It is the better shape for two reasons beyond taste. A face IS an
           oval, so a rectangle's corners cover temple and jaw hinge — the parts
           that curve hardest away from a flat projection and therefore smear
           worst when she turns. And those same corners are where a video's
           BACKDROP lives; an oval simply never shows them, which makes keying
           the background out optional rather than necessary. */
        /* ⚠ `oval` LETS THE ELLIPSE GROW PAST THE INSCRIBED ONE. Khari,
           2026-08-28: *"we are just inside of her lips, a bit of a wider oval
           should fix that."* An ellipse inscribed in the patch touches each
           edge only at its midpoint, so the corners of the mouth — which sit
           out toward the sides — fell in the fade. Above 1 it reaches past
           that, at the cost of margin: push it far enough and the oval meets
           the patch's own triangle boundary, which is a hard edge again. */
        /* ⚠ TWO RADII, BECAUSE THE FADE MUST NOT CROSS A FEATURE. Khari,
           2026-08-28: *"the only problem is the feathering near the bottom of
           her lower lip. it somewhat exposes the lips behind it due to
           transparency."* Anywhere the mask is PARTIALLY transparent, the
           model's own face shows through — which is invisible over flat cheek
           and glaring over a lip edge, because two lips are then drawn a few
           millimetres apart. The cure is not less feather (that trades one hard
           edge for another); it is moving the fade band OFF the feature, which
           needs the ellipse to stretch on one axis without growing on the
           other. */
        const dx = (u - 0.5) * 2
        const dy = (v - 0.5) * 2
        const d = Math.hypot(dx / ov, dy / ovY)
        /* ⚠ THE FADE BAND CAN BE WIDER AT THE TOP THAN ANYWHERE ELSE. Khari,
           2026-08-28: *"is there a way to apply a soft feather to just the
           top?"* Yes, and it is worth having: the four edges of this patch meet
           four different problems. The bottom crosses the lower lip, where any
           transparency doubles an edge and wants a NARROW band; the top runs
           into hairline and brow, where a hard cut-off is the visible thing and
           it wants a WIDE one.

           Blended by the DIRECTION of the point, not by its height, so the band
           widens smoothly from the sides round to the top instead of changing
           along a horizontal line — a seam in the mask is as visible as a seam
           in the video. */
        const up = d > 1e-6 ? Math.min(1, Math.max(0, (dy / ovY) / d)) : 0
        const fLocal = f + (fTop - f) * up
        a = 1 - smooth((d - (1 - fLocal)) / fLocal)
      }
      /* ⚠ AND A SECOND FADE, AT THE PATCH'S OWN BORDER. The oval closing inside
         the clip is the common case; the opposite one — a patch SMALLER than the
         oval — would end the decal on a ragged line of triangle edges, which is
         the stair-step this whole mask exists to hide. This term is 1 wherever
         the patch is roomy, so it costs nothing in the common case. */
      const pu = ax * u + bx
      const pv = ay * v + by
      a *= smooth(Math.min(pu, 1 - pu) / PATCH_FADE) * smooth(Math.min(pv, 1 - pv) / PATCH_FADE)
      const i = ((H - 1 - y) * W + x) * 4
      data[i] = data[i + 1] = data[i + 2] = Math.round(a * 255)
      data[i + 3] = 255
    }
  }
  /* ⚠ THE OUTER RING IS FORCED TO ZERO, AND IT IS LOAD-BEARING. A patch wider
     than the clip gives its vertices UVs outside 0..1, and the map is sampled
     with those same UVs — at clamp-to-edge, every one of them takes this border
     texel. Zero here means everything past the video is simply not drawn, which
     is what removes the dark halo of smeared edge pixels; any other value paints
     the video's last row across the rest of the face. A large `oval` can
     otherwise leave the border opaque, so it is not enough to rely on the
     ellipse having faded by then. */
  for (let x = 0; x < W; x++) {
    for (const y of [0, H - 1]) { const i = (y * W + x) * 4; data[i] = data[i + 1] = data[i + 2] = 0 }
  }
  for (let y = 0; y < H; y++) {
    for (const x of [0, W - 1]) { const i = (y * W + x) * 4; data[i] = data[i + 1] = data[i + 2] = 0 }
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat)
  tex.needsUpdate = true
  // Explicit rather than inherited: the row order above is already the uploaded
  // order, and a flip here would undo it.
  tex.flipY = false
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  // Explicit: the zeroed ring above only does its job under clamp.
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  return tex
}

/**
 * The real thing: her mouth, from a video, lit like the rest of her.
 *
 * ⚠ `MeshBasicMaterial` WOULD BE WRONG even though it is the obvious choice.
 * Unlit, the patch keeps the video's own brightness while the face around it
 * takes the scene's key and fill — so it glows in shadow and goes flat in
 * light, which is exactly the sticker effect. Standard material, so the same
 * lights that shade her face shade her mouth.
 */
/**
 * KEY THE BACKDROP OUT, so the corners of the patch show HER FACE rather than
 * whatever the video was shot against.
 *
 * Khari, 2026-08-28: *"or, we key out whatever color we use. if is grey, that is
 * fine too, or green.. some color that won't cause a halo or outline."*
 *
 * ⚠ THE HALO IS THE WHOLE PROBLEM, and it is why this keys on CHROMA rather
 * than on brightness. A luminance key would take the backdrop and her hair with
 * it — the patch runs up to the hairline and her hair is nearly black. Chroma
 * distance ignores how light a pixel is and asks only what colour it is, which
 * is why green and blue screens exist: no part of a face is saturated green.
 *
 * ⚠ AND IT DESPILLS. A keyed edge keeps a rim of the backdrop's colour bleeding
 * onto the subject — that rim IS the outline he wants to avoid. In the
 * transition band the colour is pulled toward its own luminance, which kills a
 * green fringe without touching the face proper.
 *
 * ⚠ THE KEY COLOUR IS CONVERTED TO LINEAR. `map_fragment` has already decoded
 * the sRGB video into the renderer's working space, so a raw hex compared
 * against it is comparing two different colour spaces and mis-keys.
 *
 * Not needed when the face genuinely fills the frame — the feather alone hides
 * the corners. It is here for when it does not.
 */
function applyChromaKey(material, { key, tolerance = 0.09, softness = 0.06 }) {
  const c = new THREE.Color(key).convertSRGBToLinear()
  const chroma = new THREE.Vector2(
    -0.169 * c.r - 0.331 * c.g + 0.5 * c.b,
    0.5 * c.r - 0.419 * c.g - 0.081 * c.b,
  )
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uKeyChroma = { value: chroma }
    shader.uniforms.uKeyTol = { value: tolerance }
    shader.uniforms.uKeySoft = { value: Math.max(0.001, softness) }
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform vec2 uKeyChroma;
        uniform float uKeyTol;
        uniform float uKeySoft;`)
      .replace('#include <map_fragment>', `#include <map_fragment>
        {
          vec3 kc = diffuseColor.rgb;
          vec2 ch = vec2(
            -0.169 * kc.r - 0.331 * kc.g + 0.5 * kc.b,
             0.5   * kc.r - 0.419 * kc.g - 0.081 * kc.b);
          float kd = distance(ch, uKeyChroma);
          float keep = smoothstep(uKeyTol, uKeyTol + uKeySoft, kd);
          diffuseColor.a *= keep;
          // Despill only where the key is still influencing the pixel.
          float lum = dot(kc, vec3(0.2126, 0.7152, 0.0722));
          diffuseColor.rgb = mix(vec3(lum), kc, clamp(keep * 1.6, 0.0, 1.0));
        }`)
    // A material whose program depends on these must not share a cached one.
    material.userData.shader = shader
  }
  material.customProgramCacheKey = () => `facedecal-key-${key}-${tolerance}-${softness}`
  return material
}

export function videoDecalMaterial(video, opts = {}) {
  const tex = new THREE.VideoTexture(video)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  return decalMaterial(tex, opts)
}

/**
 * THE SAME MATERIAL, FROM WHATEVER TEXTURE THE RUNTIME CAN GIVE IT.
 *
 * ⚠ SPLIT OUT ON 2026-09-01 FOR THE MOBILE PORT, and the split is exactly where
 * the platforms diverge. Everything below — the feather mask, the depth rule,
 * the polygon offset, the chroma key — is what makes a patch of video read as
 * part of a face rather than as a sticker, and it must be identical on every
 * surface. What CANNOT be identical is where the pixels come from: a browser
 * hands this a `VideoTexture` over a `<video>` element; React Native has no
 * video element and no path from a native player into a GL texture, so it hands
 * over a SPRITE SHEET and moves the texture's offset instead of decoding.
 *
 * ⚠ WHICH IS WHY THE TEXTURE'S OWN `repeat`/`offset` ARE LEFT ALONE HERE. The
 * sheet caller sets them per frame, and three applies a SEPARATE transform to
 * `alphaMap` — so the mask stays where it is while the picture steps through
 * cells. Reaching for a shared uv transform would tie the two together and the
 * feather would crawl around the face once per frame.
 */
export function decalMaterial(tex, {
  feather = 0.22, shape = 'oval', key = null, keyTolerance, keySoftness,
  fit, oval, ovalY, featherTop,
} = {}) {
  const material = new THREE.MeshStandardMaterial({
    map: tex,
    alphaMap: feather > 0 ? featherAlphaMap(feather, shape, { ...(fit || {}), oval, ovalY, featherTop }) : null,
    roughness: 0.75,
    metalness: 0,
    transparent: true,
    /* ⚠ IT MUST NOT WRITE DEPTH. A soft edge that still occludes leaves a
       hard silhouette in the depth buffer — the pixels fade out but nothing
       behind them can draw, so the feather reads as a smudge with a crisp
       outline. The skin underneath has already written depth; this only tints
       it. */
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  })
  return key
    ? applyChromaKey(material, {
      key,
      tolerance: Number.isFinite(keyTolerance) ? keyTolerance : undefined,
      softness: Number.isFinite(keySoftness) ? keySoftness : undefined,
    })
    : material
}
